import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/api/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/context/AuthContext'
import { useToast } from '@/components/ui/toast'
import { RecalculateScoresDialog } from './RecalculateScoresDialog'
import { ManualScoreOverrideDialog } from './ManualScoreOverrideDialog'

interface ScoringLogicConfig {
  logic?: string
  [key: string]: unknown
}

interface Dimension {
  id: string
  name: string
  slug: string
  scoring_logic_config: ScoringLogicConfig
  is_active: boolean
}

interface GoogleUsageSummary {
  monthly_spend_usd?: number
  budget_usd_monthly?: number
  items?: unknown[]
}

interface SourceType {
  id: string
  name: string
  slug: string
  archetype: string
  input_method: string
  is_active: boolean
}

interface AiConfig {
  id: string
  name: string
  provider: string
  model?: string
  is_active: boolean
}

interface AreaRow {
  id: string
  name: string
  status?: string
}

interface Signal {
  id: string
  signal_type: string
  status: string
  raw_content?: string
  fetched_at?: string
}

export function AdminScoringPage() {
  const { isAdmin } = useAuth()
  const { addToast } = useToast()
  const [activeTab, setActiveTab] = useState('dimensions')
  const [dimensions, setDimensions] = useState<Dimension[]>([])
  const [sourceTypes, setSourceTypes] = useState<SourceType[]>([])
  const [aiConfigs, setAiConfigs] = useState<AiConfig[]>([])
  const [signals, setSignals] = useState<{ items: Signal[]; total: number }>({ items: [], total: 0 })
  const [usage, setUsage] = useState<GoogleUsageSummary | null>(null)
  const [areas, setAreas] = useState<AreaRow[]>([])
  const [recalcOpen, setRecalcOpen] = useState(false)
  const [overrideOpen, setOverrideOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const [newDim, setNewDim] = useState({ name: '', slug: '', name_ar: '', scoring_logic_config: '{"logic":"weighted_average"}' })
  const [newSource, setNewSource] = useState({ name: '', slug: '', archetype: 'google_places', input_method: 'google_places_api' })

  useEffect(() => {
    if (!isAdmin) return
    loadAll()
  }, [isAdmin])

  async function loadAll() {
    setLoading(true)
    try {
      const [dims, srcs, cfgs, sigs, use, areaList] = await Promise.all([
        api.listAdminDimensions() as Promise<{ items: Dimension[] }>,
        api.listAdminSourceTypes() as Promise<{ items: SourceType[] }>,
        api.listAdminAiConfigs() as Promise<{ items: AiConfig[] }>,
        api.listAdminSignals({ limit: '50' }) as Promise<{ items: Signal[]; total: number }>,
        api.getAdminGoogleUsage() as Promise<GoogleUsageSummary>,
        api.listAdminAreas({ status: 'scoring_enabled', limit: '500' }) as Promise<{ items: AreaRow[] }>,
      ])
      setDimensions(dims.items)
      setSourceTypes(srcs.items)
      setAiConfigs(cfgs.items)
      setSignals(sigs)
      setUsage(use)
      setAreas(areaList.items || [])
    } catch (err: unknown) {
      addToast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed to load scoring data', variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  async function createDimension(e: React.FormEvent) {
    e.preventDefault()
    try {
      await api.createAdminDimension({
        ...newDim,
        scoring_logic_config: JSON.parse(newDim.scoring_logic_config) as ScoringLogicConfig,
      })
      addToast({ title: 'Dimension created' })
      setNewDim({ name: '', slug: '', name_ar: '', scoring_logic_config: '{"logic":"weighted_average"}' })
      loadAll()
    } catch (err: unknown) {
      addToast({ title: 'Error', description: err instanceof Error ? err.message : undefined, variant: 'error' })
    }
  }

  async function createSourceType(e: React.FormEvent) {
    e.preventDefault()
    try {
      await api.createAdminSourceType(newSource)
      addToast({ title: 'Source type created' })
      setNewSource({ name: '', slug: '', archetype: 'google_places', input_method: 'google_places_api' })
      loadAll()
    } catch (err: unknown) {
      addToast({ title: 'Error', description: err instanceof Error ? err.message : undefined, variant: 'error' })
    }
  }

  async function verifySignal(id: string) {
    try {
      await api.verifyAdminSignal(id)
      addToast({ title: 'Signal verified' })
      loadAll()
    } catch (err: unknown) {
      addToast({ title: 'Error', description: err instanceof Error ? err.message : undefined, variant: 'error' })
    }
  }

  async function rejectSignal(id: string) {
    try {
      await api.rejectAdminSignal(id, 'Rejected from admin')
      addToast({ title: 'Signal rejected' })
      loadAll()
    } catch (err: unknown) {
      addToast({ title: 'Error', description: err instanceof Error ? err.message : undefined, variant: 'error' })
    }
  }

  if (!isAdmin) {
    return <div className="container py-8 text-sm text-red-500">Platform admin access required.</div>
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Area Scoring Admin</h1>
        <Button size="sm" onClick={() => setRecalcOpen(true)}>
          Recalculate scores
        </Button>
        <Button size="sm" variant="outline" onClick={() => setOverrideOpen(true)}>
          Manual score override
        </Button>
      </div>
      {loading && <p className="mb-4 text-sm text-muted-foreground">Loading...</p>}

      <RecalculateScoresDialog
        open={recalcOpen}
        onOpenChange={setRecalcOpen}
        areas={areas}
        dimensions={dimensions}
        onStarted={loadAll}
      />
      <ManualScoreOverrideDialog
        open={overrideOpen}
        onOpenChange={setOverrideOpen}
        areas={areas}
        dimensions={dimensions}
        onSubmitted={loadAll}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dimensions">Dimensions</TabsTrigger>
          <TabsTrigger value="sources">Source Types</TabsTrigger>
          <TabsTrigger value="ai">AI Configs</TabsTrigger>
          <TabsTrigger value="signals">Signals</TabsTrigger>
          <TabsTrigger value="usage">Google Usage</TabsTrigger>
        </TabsList>

        <TabsContent value="dimensions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Dimensions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4 max-h-96 overflow-auto">
                {dimensions.map((d) => (
                  <div key={d.id} className="flex items-center justify-between border-b py-2 text-sm">
                    <div>
                      <span className="font-medium">{d.name}</span>
                      <span className="ms-2 text-muted-foreground">{d.slug}</span>
                      <span className="ms-2 rounded bg-gray-100 px-1 text-xs">{d.scoring_logic_config?.logic}</span>
                    </div>
                    <span className="text-xs">{d.is_active ? 'Active' : 'Inactive'}</span>
                  </div>
                ))}
              </div>
              <form onSubmit={createDimension} className="space-y-2">
                <div className="grid gap-2 md:grid-cols-2">
                  <Input placeholder="Name" value={newDim.name} onChange={(e) => setNewDim({ ...newDim, name: e.target.value })} />
                  <Input placeholder="Slug" value={newDim.slug} onChange={(e) => setNewDim({ ...newDim, slug: e.target.value })} />
                  <Input placeholder="Arabic name" value={newDim.name_ar} onChange={(e) => setNewDim({ ...newDim, name_ar: e.target.value })} />
                  <Input placeholder="Scoring logic JSON" value={newDim.scoring_logic_config} onChange={(e) => setNewDim({ ...newDim, scoring_logic_config: e.target.value })} />
                </div>
                <Button type="submit" size="sm">Add Dimension</Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sources" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Source Types</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4 max-h-96 overflow-auto">
                {sourceTypes.map((s) => (
                  <div key={s.id} className="flex items-center justify-between border-b py-2 text-sm">
                    <div>
                      <span className="font-medium">{s.name}</span>
                      <span className="ms-2 text-muted-foreground">{s.slug}</span>
                      <span className="ms-2 rounded bg-gray-100 px-1 text-xs">{s.archetype}</span>
                    </div>
                    <span className="text-xs">{s.input_method}</span>
                  </div>
                ))}
              </div>
              <form onSubmit={createSourceType} className="space-y-2">
                <div className="grid gap-2 md:grid-cols-3">
                  <Input placeholder="Name" value={newSource.name} onChange={(e) => setNewSource({ ...newSource, name: e.target.value })} />
                  <Input placeholder="Slug" value={newSource.slug} onChange={(e) => setNewSource({ ...newSource, slug: e.target.value })} />
                  <Input placeholder="Input method" value={newSource.input_method} onChange={(e) => setNewSource({ ...newSource, input_method: e.target.value })} />
                </div>
                <Button type="submit" size="sm">Add Source Type</Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ai" className="space-y-4">
          <Card data-screen="PA-SCR-002-entry">
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle>AI configs</CardTitle>
                <Button asChild size="sm">
                  <Link to="/admin/scoring/ai-configs">Open AI config console</Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Version prompts, run metered previews, and activate configs in the dedicated PA-SCR-002 console.
              </p>
              <div className="max-h-96 overflow-auto">
                {aiConfigs.map((c) => (
                  <div key={c.id} className="flex items-center justify-between border-b py-2 text-sm">
                    <div>
                      <span className="font-medium">{c.name}</span>
                      <span className="ms-2 text-muted-foreground">{c.provider}</span>
                      <span className="ms-2 text-muted-foreground">{c.model}</span>
                    </div>
                    <span className="text-xs">{c.is_active ? 'Active' : 'Inactive'}</span>
                  </div>
                ))}
                {aiConfigs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No AI configs yet.</p>
                ) : null}
              </div>
              <Button asChild variant="outline" size="sm">
                <Link to="/admin/scoring/ai-configs">Manage in AI config console</Link>
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="signals" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Signal Review Queue</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-96 overflow-auto">
                {signals.items.map((s) => (
                  <div key={s.id} className="flex items-center justify-between border-b py-2 text-sm">
                    <div>
                      <div className="font-medium">{s.signal_type}</div>
                      <div className="text-xs text-muted-foreground">{s.status} · {s.fetched_at ? new Date(s.fetched_at).toLocaleString() : ''}</div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => verifySignal(s.id)}>Verify</Button>
                      <Button size="sm" variant="outline" onClick={() => rejectSignal(s.id)}>Reject</Button>
                    </div>
                  </div>
                ))}
                {signals.items.length === 0 && <p className="text-sm text-muted-foreground">No signals pending.</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="usage" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Google Maps Usage</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm">
                <div>Monthly spend: <strong>${usage?.monthly_spend_usd?.toFixed?.(4) ?? usage?.monthly_spend_usd ?? '—'}</strong></div>
                <div>Budget: <strong>${usage?.budget_usd_monthly ?? '—'}</strong></div>
                <div className="mt-2 text-muted-foreground">{usage?.items?.length ?? 0} usage records</div>
                <Link
                  to="/admin/google-usage"
                  className="mt-3 inline-block text-[var(--lc-action-primary)] underline underline-offset-2"
                >
                  Open full usage &amp; budget dashboard →
                </Link>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
